import React, { useState } from 'react';
import { X, Mail, Lock, Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { useSettings } from './SettingsContext';
import { trackEvent } from '../lib/analytics';

interface AuthModalProps {
  onClose: () => void;
  isAnonymous: boolean;
  loginWithGoogle: () => Promise<void>;
  linkWithGoogle: () => Promise<void>;
  registerAndLinkEmail: (email: string, password: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
}

export default function AuthModal({
  onClose,
  isAnonymous,
  loginWithGoogle,
  linkWithGoogle,
  registerAndLinkEmail,
  loginWithEmail,
}: AuthModalProps) {
  const { playSfx, triggerHaptic } = useSettings();
  const [mode, setMode] = useState<'link' | 'login'>(isAnonymous ? 'link' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getFriendlyErrorMessage = (code: string) => {
    switch (code) {
      case 'auth/invalid-email': return 'Indirizzo email non valido.';
      case 'auth/weak-password': return 'La password deve essere di almeno 6 caratteri.';
      case 'auth/email-already-in-use': return 'Questo indirizzo email è già associato a un altro account.';
      case 'auth/credential-already-in-use':
      case 'auth/account-exists-with-different-credential':
        return 'Questo account (Google o email) è già associato a un altro giocatore.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Email o password non valide.';
      case 'auth/popup-closed-by-user': return 'Accesso con Google annullato dall\'utente.';
      default: return 'Si è verificato un errore durante l\'autenticazione. Riprova.';
    }
  };

  const handleGoogleAuth = async () => {
    playSfx('click');
    triggerHaptic('light');
    setLoading(true);
    setError(null);
    const method = mode === 'link' && isAnonymous ? 'link_google' : 'google';
    trackEvent('registration_start', { method, source: 'auth_modal' });

    try {
      if (mode === 'link' && isAnonymous) {
        await linkWithGoogle();
      } else {
        await loginWithGoogle();
      }
      trackEvent('registration_complete', {
        method,
        source: 'auth_modal',
        account_type: mode === 'link' ? 'linked' : 'registered',
      });
      onClose();
    } catch (authError: any) {
      setError(getFriendlyErrorMessage(authError.code || authError.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    playSfx('click');
    triggerHaptic('light');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Inserisci tutti i campi richiesti.');
      return;
    }

    const method = mode === 'link' ? 'email_register' : 'email_login';
    trackEvent('registration_start', { method, source: 'auth_modal' });
    setLoading(true);
    setError(null);

    try {
      if (mode === 'link') {
        await registerAndLinkEmail(trimmedEmail, password);
      } else {
        await loginWithEmail(trimmedEmail, password);
      }
      trackEvent('registration_complete', {
        method,
        source: 'auth_modal',
        account_type: mode === 'link' ? 'linked' : 'registered',
      });
      onClose();
    } catch (authError: any) {
      setError(getFriendlyErrorMessage(authError.code || authError.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.95, y: 15, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 15, opacity: 0 }}
          className="w-full max-w-md glass-panel rounded-3xl relative border border-white/10 flex flex-col p-6 overflow-hidden my-8"
        >
          <div className="absolute -right-12 -top-12 w-32 h-32 bg-yellow-400/5 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute -left-12 -bottom-12 w-32 h-32 bg-green-400/5 rounded-full blur-2xl pointer-events-none" />

          <button
            onClick={() => {
              playSfx('click');
              triggerHaptic('light');
              onClose();
            }}
            disabled={loading}
            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors disabled:opacity-50"
            aria-label="Chiudi autenticazione"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>

          <div className="text-center space-y-1.5 mb-6 pr-6">
            <h2 className="text-2xl font-black uppercase text-yellow-400 tracking-wider" style={{ fontFamily: '"Playfair Display", serif' }}>
              {mode === 'link' ? 'Salva Progressi' : 'Accedi al Profilo'}
            </h2>
            <p className="text-xs text-white/50 leading-relaxed">
              {mode === 'link'
                ? 'Associa il profilo ospite a credenziali permanenti per continuare da altri dispositivi.'
                : 'Accedi per ripristinare livello, statistiche e funzioni sociali.'}
            </p>
          </div>

          <button
            onClick={handleGoogleAuth}
            disabled={loading}
            className="w-full py-3.5 px-4 bg-white hover:bg-white/95 text-black font-bold rounded-2xl transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-3 shadow-lg disabled:opacity-50 disabled:pointer-events-none shrink-0"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-black" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
            )}
            <span className="text-sm">{mode === 'link' ? 'Collega con Google' : 'Accedi con Google'}</span>
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase font-bold tracking-widest text-white/30 shrink-0">oppure con email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleEmailAuth} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="auth-email" className="text-[10px] uppercase tracking-wider font-bold text-white/40 ml-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input id="auth-email" type="email" autoComplete="email" placeholder="inserisci la tua email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={loading} className="w-full pl-11 pr-4 py-3 bg-black/40 border border-white/10 hover:border-white/20 focus:border-yellow-500/50 rounded-2xl text-sm text-white placeholder-white/20 focus:outline-none transition-all disabled:opacity-50" required />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="auth-password" className="text-[10px] uppercase tracking-wider font-bold text-white/40 ml-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input id="auth-password" type="password" autoComplete={mode === 'link' ? 'new-password' : 'current-password'} placeholder={mode === 'link' ? 'crea una password (min. 6 caratteri)' : 'inserisci la tua password'} value={password} onChange={(event) => setPassword(event.target.value)} disabled={loading} className="w-full pl-11 pr-4 py-3 bg-black/40 border border-white/10 hover:border-white/20 focus:border-yellow-500/50 rounded-2xl text-sm text-white placeholder-white/20 focus:outline-none transition-all disabled:opacity-50" required />
              </div>
            </div>

            {error && (
              <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-red-400 font-medium ml-1" role="alert">
                {error}
              </motion.p>
            )}

            <button type="submit" disabled={loading} className="w-full py-3.5 bg-yellow-500 hover:bg-yellow-400 text-black font-black rounded-2xl uppercase tracking-wider text-xs transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin text-black" /> : mode === 'link' ? 'Registrati e Collega' : 'Accedi'}
            </button>
          </form>

          <div className="text-center mt-6">
            <button
              onClick={() => {
                playSfx('click');
                triggerHaptic('light');
                setMode(mode === 'link' ? 'login' : 'link');
                setError(null);
              }}
              disabled={loading}
              className="text-xs text-white/40 hover:text-white/80 transition-colors"
            >
              {mode === 'link' ? 'Hai già un account registrato? Accedi qui' : 'Vuoi proteggere un profilo ospite? Registrati / Collega'}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
